import DateSelectorMode, {
    SINGLE_LINE_SAME_DAY,
    MULTILINE_WITH_TIME,
    SINGLE_LINE_DURATION,
    MULTILINE_DURATION,
    MULTILINE,
    MULTILINE_WITH_TIMEZONES,
    SINGLE_LINE_ALL_DAY,
} from '../types/DateSelectorMode';
import { DurationDayLength } from '../components/DurationDropdown';
import { isFeatureEnabled } from 'owa-feature-flags';

/**
 * Method to set the next selectorMode state
 *  State diagram can be found here https://msft.spoppe.com/teams/EXOcd/_layouts/15/WopiFrame.aspx?sourcedoc={3902e207-6f4c-42a5-8636-954b5cd3f567}&action=edit&wd=target%28%2F%2FReact%20Calendar.one%7Ca80c63bb-dd3d-4969-9dc7-2c959e36dae3%2FDate%20Selection%20State%20Diagram%7C03a92b79-1cf7-48f5-aa4e-37a3096b520c%2F%29
 * @param isNarrow the width of the dateSelector (it is in Narrow when in edit)
 * @param currentSelectorMode the current mode that the date selector is in
 * @param isCustom set this to true when the new mode is being set by a custom selection from the duration or time picker
 * @param durationInDays The updated duration of the event. If the duration update has not trigger the function leave it null
 * @param showTimeZones whether to show time zones in the selector
 * @param isAllDay optional. Include the new isAllDay value when a new mode needs to be set, exclude when the value hasn't changed
 * @returns the new date selector mode if one is set and null if we dont change modes
 */
export default function updateSelectorMode(
    isNarrow: boolean,
    currentSelectorMode: DateSelectorMode,
    isCustom: boolean,
    durationInDays: number,
    showTimeZones: boolean,
    preferEndDateToBeShown: boolean,
    isAllDay?: boolean
): DateSelectorMode {
    let nextSelectorMode: DateSelectorMode = null;

    if (showTimeZones && !isNarrow && (isAllDay == null || !isAllDay)) {
        nextSelectorMode = MULTILINE_WITH_TIMEZONES;
    } else {
        switch (currentSelectorMode) {
            case SINGLE_LINE_SAME_DAY:
                if (isAllDay) {
                    nextSelectorMode = isFeatureEnabled('mon-cal-dateTimeInEdit')
                        ? SINGLE_LINE_ALL_DAY
                        : SINGLE_LINE_DURATION;
                } else if (isCustom || durationInDays != DurationDayLength.One || isNarrow) {
                    nextSelectorMode = MULTILINE_WITH_TIME;
                }
                break;
            case MULTILINE_WITH_TIME:
                if (isAllDay == true) {
                    if (isFeatureEnabled('mon-cal-dateTimeInEdit')) {
                        nextSelectorMode = SINGLE_LINE_ALL_DAY;
                    } else if (durationInDays == DurationDayLength.One) {
                        nextSelectorMode = SINGLE_LINE_DURATION;
                    } else {
                        nextSelectorMode = MULTILINE_DURATION;
                    }
                } else if (showTimeZones) {
                    nextSelectorMode = MULTILINE_WITH_TIMEZONES;
                } else if (
                    !isNarrow &&
                    durationInDays == DurationDayLength.One &&
                    !preferEndDateToBeShown
                ) {
                    nextSelectorMode = SINGLE_LINE_SAME_DAY;
                }
                break;
            case MULTILINE_WITH_TIMEZONES:
                if (!showTimeZones) {
                    if (
                        isNarrow ||
                        durationInDays != DurationDayLength.One ||
                        preferEndDateToBeShown
                    ) {
                        nextSelectorMode = MULTILINE_WITH_TIME;
                    } else if (durationInDays == DurationDayLength.One) {
                        nextSelectorMode = SINGLE_LINE_SAME_DAY;
                    }
                }
                break;
            case SINGLE_LINE_DURATION:
                if (isAllDay == false) {
                    if (
                        isNarrow ||
                        durationInDays != DurationDayLength.One ||
                        preferEndDateToBeShown
                    ) {
                        nextSelectorMode = MULTILINE_WITH_TIME;
                    } else if (durationInDays == DurationDayLength.One) {
                        nextSelectorMode = SINGLE_LINE_SAME_DAY;
                    }
                } else if (isAllDay == null) {
                    // Only duration was set
                    if (isCustom) {
                        nextSelectorMode = MULTILINE;
                    } else if (durationInDays != DurationDayLength.One) {
                        nextSelectorMode = MULTILINE_DURATION;
                    }
                }
                break;
            case MULTILINE_DURATION:
                if (isAllDay == false) {
                    nextSelectorMode = MULTILINE_WITH_TIME;
                } else if (isAllDay == null) {
                    // Only duration was set
                    if (isCustom) {
                        nextSelectorMode = MULTILINE;
                    } else if (durationInDays == DurationDayLength.One) {
                        nextSelectorMode = SINGLE_LINE_DURATION;
                    }
                }
                break;
            case MULTILINE:
                if (isAllDay == false) {
                    if (
                        isNarrow ||
                        durationInDays != DurationDayLength.One ||
                        preferEndDateToBeShown
                    ) {
                        nextSelectorMode = MULTILINE_WITH_TIME;
                    } else {
                        nextSelectorMode = SINGLE_LINE_SAME_DAY;
                    }
                }
                break;
            case SINGLE_LINE_ALL_DAY:
                if (isAllDay == false) {
                    if (
                        isNarrow ||
                        durationInDays != DurationDayLength.One ||
                        preferEndDateToBeShown
                    ) {
                        nextSelectorMode = MULTILINE_WITH_TIME;
                    } else {
                        nextSelectorMode = SINGLE_LINE_SAME_DAY;
                    }
                }
                break;
            default:
                break;
        }
    }

    return nextSelectorMode;
}
